//Hooks
import { get } from 'svelte/store';
//Formatters
import { format } from 'svelte-i18n';
//Helpers
import { capFirstChar } from 'src/helpers/utilities';
import { isBluTRV } from 'src/helpers/interface_helpers';
import {
	bthomeObjectInfos,
	getBthomeDeviceModel,
} from 'src/helpers/virtual_helpers/virtual_bthome_helper';
import {
	getTemplateServiceByDeviceID,
	getStatusIconByRole,
	getTemplateService,
	isXT1Module,
	translateTemplateTitle,
} from 'src/helpers/xt1_helpers/template_helpers';
//Stores
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import {
	getComponentByDevice,
	getDeviceIDByComponent,
} from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { getWebIconStatuses } from 'src/helpers/virtual_helpers/virtual_icons_helpers';
import { bluTrvNewWebhookSupport } from '../supported/webhook_list_supported_helpers';
//Components
import type {
	TemplateService,
	VirtualActionUI,
	VirtualComponentConfig,
	VirtualComponentUI,
	VirtualConditionUI,
	VirtualWebIcons,
} from 'src/types/VirtualComponents';
//Types
import type { Device } from 'src/types/Device';
import type { WebHooksParams } from 'src/types/requests/settings/webhooks';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';

export const getVirtualWebHookList = (device: Device, allChannels = false): string[] => {
	let vGroupComponents: string[];
	const deviceStatus: DeviceStatusG2 = devices_status.getByRealId(device.id);
	const devicesSettings: DeviceSettingsG2 = devices_settings.getByRealId(device.id);
	if (allChannels) {
		vGroupComponents = Object.keys(deviceStatus || {})
			.filter((key) =>
				/^(boolean|enum|text|button|number|bthomesensor|bthomedevice):2\d{2}$/.test(key),
			)
			.sort();
	} else if (isXT1Module(device.type)) {
		vGroupComponents = getTemplateService(device).components.map((component) => component.key);
	} else {
		vGroupComponents = deviceStatus?.[`group:${device.channel}`]?.value || [];
		if (/^(SBTR-001AEU|bthome-device)$/.test(device.type)) {
			const component = getComponentByDevice(device);
			const compAddr = devicesSettings?.[component]?.addr;
			vGroupComponents = [];
			for (const component in devicesSettings) {
				if (
					/^(bthomesensor):2\d{2}$/.test(component) &&
					devicesSettings[component]?.addr == compAddr
				) {
					vGroupComponents.push(component);
				}
			}
			if (/^(blutrv):2\d{2}$/.test(component) && bluTrvNewWebhookSupport(device.id)) {
				vGroupComponents.unshift(component);
			} else if (
				/^(bthomedevice):2\d{2}$/.test(component) &&
				getBthomeDeviceModel(deviceStatus?.[component])
			) {
				vGroupComponents.unshift(component);
			}
		}
	}
	return vGroupComponents;
};

export const getVirtualWebHookTypes = (device: Device, allChannels = false): WebHooksParams => {
	const vGroupComponents: string[] = getVirtualWebHookList(device, allChannels);
	const devicesSettings: DeviceSettingsG2 = devices_settings.getByRealId(device.id);
	const components: WebHooksParams['components'] = getVirtualComponentsActions(
		vGroupComponents,
		devicesSettings,
		true,
		device,
	);
	return { components };
};

export const getVirtualComponentsActions = (
	components: string[],
	deviceSettings: DeviceSettingsG2,
	webhookLocalAction = false,
	device: Device = null,
): WebHooksParams['components'] => {
	const options: WebHooksParams['components'] = [];
	const $format = get(format);
	const templateService: TemplateService = device
		? getTemplateService(device)
		: getTemplateServiceByDeviceID(deviceSettings?.id);
	const bthomeSensorsCounts: number[] = [];
	for (let i = 0; i < components.length; ++i) {
		const component: string = components[i] || '';
		const splitComponent: string[] = component.split(':');
		const type = splitComponent[0];
		const config: VirtualComponentConfig = deviceSettings?.[component];
		const configUI: VirtualComponentUI = config?.meta?.ui || {};
		let enums: WebHooksParams['enums'] = null;
		let title = deviceSettings?.[component]?.name;
		let inputType: string = null;
		let icon: string = null;
		let unit: string = '';
		let role: string = '';
		if (!config || (!webhookLocalAction && config?.access && !/(w|\*)/.test(config.access))) {
			continue;
		}
		const webIconStatuses: VirtualWebIcons[] = getWebIconStatuses();
		if (/^(bthomesensor)$/.test(type)) {
			const bthomeObj = bthomeObjectInfos.find((i) => i.id == config?.obj_id);
			if (bthomeObj) {
				inputType = 'value_change';
				unit = bthomeObj.unit;
				const webIcon = webIconStatuses.find(
					(i) => i.bthome_obj && i.bthome_obj.includes(bthomeObj.name),
				);
				title = config.name || webIcon?.title || bthomeObj.name;
				if (/^(SBTR-001AEU)$/.test(device?.type) && bthomeObj.id === 69) {
					let tempValue =
						config.idx === 0
							? $format('label_target_temperature', {
									default: 'Target temperature',
								})
							: config.idx === 1
								? $format('label_current_temperature', {
										default: 'Current temperature',
									})
								: config.idx === 2
									? $format('label_external_temperature', {
											default: 'External temperature',
										})
									: title;
					title = tempValue;
				}

				icon = webIcon?.icon;
				if (bthomeObj.name == 'button') {
					inputType = 'single_push|double_push|triple_push|long_push';
				} else if (bthomeObj.type == 'binary_sensor') {
					inputType = 'state_change';
					enums = [
						{
							title: webIcon?.label_off || 'false',
							value: false,
							icon: webIcon?.icon_off,
						},
						{
							title: webIcon?.label_on || 'true',
							value: true,
							icon: webIcon?.icon_on,
						},
					];
				}
				if (!config.name && config?.obj_id) {
					const sensorCounts = bthomeSensorsCounts.filter((i) => i == config.obj_id).length;
					if (sensorCounts > 0 && !(isBluTRV(device?.type) && bthomeObj.id === 69))
						title += ` (${sensorCounts})`;
					bthomeSensorsCounts.push(config.obj_id);
				}
			} else continue;
		} else if (templateService?.type && config?.owner && templateService?.ui?.actions) {
			role =
				templateService.components.find((i) => i.key == component)?.status?._attrs?.role || '';
			let action: VirtualConditionUI = null;
			if (webhookLocalAction) {
				const condition = templateService.ui.conditions[role];
				if (!condition) continue;
				action = templateService.ui.conditions[role];
			} else {
				const actions = Object.values(templateService.ui.actions);
				const localAction: VirtualActionUI = actions.find((i) => i.component == role);
				if (!localAction) continue;
				action = { name: localAction.name, event: localAction.params?.event };
			}
			let webIcon: VirtualWebIcons;
			if (/^(window-cover)$/.test(templateService.type)) {
				webIcon = webIconStatuses?.[19];
			} else if (templateService.type == 'irrigation-controller') {
				webIcon = webIconStatuses?.[20];
			} else if (templateService.type == 'simple-water-valve-controller') {
				webIcon = webIconStatuses?.[21];
			}
			icon = webIcon?.icon || getStatusIconByRole(role) || null;
			unit = configUI?.unit || config?.unit || '';
			title =
				translateTemplateTitle(action.name || config?.name) ||
				capFirstChar(role.replace(/_/g, ' '));
			if (action.event) {
				enums = [
					{
						title: translateTemplateTitle(action.name || title),
						value: action.event,
					},
				];
			} else if (type == 'boolean') {
				enums = [
					{
						title:
							translateTemplateTitle(configUI?.titles?.[0]) ||
							webIcon?.label_off ||
							'false',
						value: false,
						icon: webIcon?.icon_off,
					},
					{
						title:
							translateTemplateTitle(configUI?.titles?.[1]) || webIcon?.label_on || 'true',
						value: true,
						icon: webIcon?.icon_on,
					},
				];
			} else if (type == 'enum' && config?.options?.length) {
				enums = [];
				config.options.forEach((i) => {
					enums.push({
						title: translateTemplateTitle(configUI?.titles?.[i] || i),
						value: !webhookLocalAction ? i : `"${i}"`,
					});
				});
			}
		} else {
			const webIcon: VirtualWebIcons = webIconStatuses?.[configUI?.webIcon];
			unit = configUI?.unit || config?.unit || '';
			icon = webIcon?.icon;
			title = config?.name || `${type} (${config?.id})`;
			if (type == 'boolean') {
				enums = [
					{
						title: configUI?.titles?.[0] || webIcon?.label_off || 'false',
						value: false,
						icon: webIcon?.icon_off,
					},
					{
						title: configUI?.titles?.[1] || webIcon?.label_on || 'true',
						value: true,
						icon: webIcon?.icon_on,
					},
				];
			} else if (type == 'enum' && config?.options?.length) {
				enums = [];
				config.options.forEach((i) => {
					enums.push({
						title: configUI?.titles?.[i] || i,
						value: !webhookLocalAction ? i : `"${i}"`,
					});
				});
			} else if (type == 'button') {
				icon = 'far fa-hand-back-point-up';
				enums = [
					{
						title: $format('option_input_button_push', {
							default: 'Button is pushed',
						}),
						value: 'single_push',
					},
					{
						title: $format('option_input_button_doublepush', {
							default: 'Button is double pushed',
						}),
						value: 'double_push',
					},
					{
						title: $format('option_input_button_triplepush', {
							default: 'Button is triple pushed',
						}),
						value: 'triple_push',
					},
					{
						title: $format('option_input_button_longpush', {
							default: 'Button is long pushed',
						}),
						value: 'long_push',
					},
				];
			}
		}
		if (/^(blutrv)$/.test(type)) {
			title = get(devices)[getDeviceIDByComponent(deviceSettings?.id, component)]?.name || title;
		} else if (/^(bthomedevice)$/.test(type)) {
			title = $format('input_type_button', {
				default: 'Button',
			});
		}
		options.push({
			title,
			type,
			component,
			inputType,
			enums,
			unit,
			config,
			icon,
			role,
			cid: config?.id,
		});
	}
	return options;
};
